import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "../../../../context/ThemeContext";

/**
 * Google-Flights-style tab toggle.
 *
 * Usage:
 *   <TransportTabToggle
 *     tabs={[
 *       { key: "available", label: "Saatavilla olevat vaihtoehdot" },
 *       { key: "custom",    label: "Luo oma" },
 *     ]}
 *     activeTab="available"
 *     onChange={(key) => setActiveTab(key)}
 *   />
 */
export const TransportTabToggle = ({ tabs, activeTab, onChange }) => {
  const { isDark } = useTheme();

  return (
    <Box
      role="tablist"
      sx={{
        display: "inline-flex",
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid",
        borderColor: isDark ? "rgba(255,255,255,0.12)" : "grey.300",
        bgcolor: isDark ? "rgba(255,255,255,0.03)" : "grey.50",
        mb: 2,
        width: "100%",
      }}
    >
      {tabs.map((tab) => {
        const isActive = tab.key === activeTab;

        return (
          <Box
            key={tab.key}
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(tab.key)}
            sx={{
              flex: 1,
              px: { xs: 1.5, sm: 2.5 },
              py: 1,
              cursor: "pointer",
              position: "relative",
              textAlign: "center",
              userSelect: "none",
              transition: "all 0.15s ease",
              bgcolor: isActive
                ? isDark
                  ? "primary.dark"
                  : "primary.main"
                : "transparent",
              "&:hover": {
                bgcolor: isActive
                  ? undefined
                  : isDark
                    ? "rgba(255,255,255,0.06)"
                    : "rgba(0,0,0,0.04)",
              },
              // Divider between tabs (only between, not on edges)
              "&:not(:last-of-type)": {
                borderRight: "1px solid",
                borderRightColor: isDark ? "rgba(255,255,255,0.12)" : "grey.300",
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: isActive ? 700 : 500,
                fontSize: { xs: "0.8rem", sm: "0.85rem" },
                color: isActive
                  ? "primary.contrastText"
                  : "text.secondary",
                whiteSpace: "nowrap",
                transition: "color 0.15s",
              }}
            >
              {tab.label}
            </Typography>
            {tab.subtitle && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  fontSize: "0.7rem",
                  color: isActive
                    ? "rgba(255,255,255,0.8)"
                    : "text.disabled",
                  mt: -0.25,
                }}
              >
                {tab.subtitle}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};